'use client';

import { ArrowUpRight } from 'lucide-react';

const PRESTIGIOUS_PRODUCTS = [
  {
    name: 'Cabas Alma',
    category: 'Sacs à Main',
    price: 'dès 480 €',
    image: '/images/product-cabas-alma.png',
    desc: 'Cabas structuré en cuir pleine fleur, anses rouleautées, doublure suédine, fond renforcé. Le sac du quotidien qui s\'embellit avec les années.',
    signature: 'Couture sellier · Garantie à vie',
  },
  {
    name: 'Ceinture Maréchal',
    category: 'Ceintures',
    price: 'dès 165 €',
    image: '/images/product-ceinture-marechal.png',
    desc: 'Ceinture sellier 3,5 cm, boucle dorée ciselée à la main, cuir tanné végétal patiné à la cire d\'abeille. Le détail qui signe l\'allure.',
    signature: 'Boucle gravée · Numérotée',
  },
  {
    name: 'Portefeuille Auguste',
    category: 'Petite Maroquinerie',
    price: 'dès 145 €',
    image: '/images/product-portefeuille-auguste.png',
    desc: 'Pliage cuir cognac, huit emplacements cartes, deux poches billets, poche à monnaie. Format intemporel, patine personnalisée.',
    signature: 'Peau véritable · 8 slots',
  },
  {
    name: 'Sac à Dos Explorateur',
    category: 'Sacs & Bagagerie',
    price: 'dès 690 €',
    image: '/images/product-sac-dos-explorateur.png',
    desc: 'Sac à dos en cuir espresso, sangles réglables, fermoir laiton, doublure coton. Compagnon de voyage pour la vie entière.',
    signature: 'Laiton massif · Édition voyage',
  },
  {
    name: 'Bottines Augusta',
    category: 'Chaussures',
    price: 'dès 540 €',
    image: '/images/product-bottines-augusta.png',
    desc: 'Bottines montées Goodyear, cuir patiné main, œillets laiton, lacets cuir tressé. L\'élégance anglaise, le tempérament d\'Anvers.',
    signature: 'Montage Goodyear · Patine main',
  },
  {
    name: 'Collection Essentielle',
    category: 'Coffrets Offerts',
    price: 'dès 1 280 €',
    image: '/images/product-coffret-essentielle.png',
    desc: 'Écrin noir laqué révélant un assortiment sur mesure : ceinture, portefeuille, porte-cartes, gants. Le cadeau de maison par excellence.',
    signature: 'Écrin gravé · Sur mesure',
  },
];

const ALL_CATEGORIES = [
  { name: 'Sacs à Main', count: 50, icon: '👜' },
  { name: 'Ceintures', count: 50, icon: '➰' },
  { name: 'Petite Maroquinerie', count: 50, icon: '⬛' },
  { name: 'Sacs & Bagagerie', count: 50, icon: '🎒' },
  { name: 'Chaussures', count: 50, icon: '👞' },
  { name: 'Coffrets & Accessoires', count: 50, icon: '🎁' },
  { name: 'Manteaux & Vestes', count: 50, icon: '🧥' },
  { name: 'Pantalons', count: 50, icon: '👖' },
  { name: 'Jupes', count: 50, icon: '👗' },
  { name: 'Gilets', count: 50, icon: '🧵' },
  { name: 'Écharpes & Foulards', count: 50, icon: '🧣' },
  { name: 'Nattes de Prière', count: 50, icon: '🕌' },
  { name: 'Fourrures & Peaux', count: 50, icon: '🦊' },
  { name: 'Cartables & Sacs Écoliers', count: 50, icon: '📚' },
  { name: 'Sur Mesure', count: '∞', icon: '✦' },
];

export default function PrestigiousProducts() {
  return (
    <section
      id="exception"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Background gold radial */}
      <div
        className="absolute -top-1/3 -right-1/4 w-[700px] h-[700px] rounded-full opacity-[0.06] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Produits d'Exception</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Pièces maîtresses</span>
            <br />
            <span className="text-[#F4E8C1] text-2xl sm:text-3xl lg:text-4xl font-light tracking-[0.08em]">
              de la maison
            </span>
          </h2>
          <p className="max-w-3xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            Découvrez nos pièces maîtresses : sac, ceinture, portefeuille assortis.
            Chaque création est façonnée à la main dans nos ateliers d'Anvers,
            numérotée, livrée avec certificat d'authenticité et garantie à vie.
            <span className="block mt-3 text-[#D4AF37]/80 italic font-serif text-sm">
              « C'est ce qui distingue un sac qui dure dix ans d'un sac qui dure cent ans. »
            </span>
          </p>
        </div>

        {/* Products grid — 3 cols on desktop, alternating heights for editorial feel */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 mb-20">
          {PRESTIGIOUS_PRODUCTS.map((p, idx) => (
            <article
              key={p.name}
              className="lift-card group relative overflow-hidden bg-[#12121a] border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 transition-colors duration-500"
            >
              {/* Image */}
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={`${p.name} — ${p.category} MATAR STYLE CUIR`}
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                  style={{ filter: 'brightness(0.85) saturate(1.08)' }}
                />
                <div
                  className="absolute inset-0 opacity-70"
                  style={{
                    background:
                      'radial-gradient(ellipse at center, transparent 40%, rgba(10,10,15,0.7) 100%)',
                  }}
                />
                {/* Price badge — top right */}
                <div className="absolute top-4 right-4 bg-gold-gradient text-[#0a0a0f] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">
                  {p.price}
                </div>
                {/* Category — top left */}
                <div className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.25em] text-[#F4E8C1]/85 bg-[#0a0a0f]/60 backdrop-blur-sm px-2.5 py-1 border border-[#D4AF37]/25">
                  {p.category}
                </div>
                {/* Number badge */}
                <div className="absolute bottom-4 left-4 font-display text-[#D4AF37] text-2xl font-bold opacity-80">
                  {String(idx + 1).padStart(2, '0')}
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                <h3 className="font-display text-xl font-semibold text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors duration-300 mb-3 tracking-wide">
                  {p.name}
                </h3>
                <p className="text-sm text-[#F4E8C1]/60 leading-relaxed font-light mb-5">
                  {p.desc}
                </p>

                {/* Signature line */}
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-[#D4AF37]/15">
                  <span className="text-[#D4AF37] text-xs">✦</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/85 font-medium">
                    {p.signature}
                  </span>
                </div>

                {/* CTA */}
                <button className="w-full inline-flex items-center justify-center gap-2 py-3 border border-[#D4AF37]/30 text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1] hover:bg-[#D4AF37] hover:text-[#0a0a0f] hover:border-[#D4AF37] transition-all duration-500 font-medium">
                  Demander cette pièce
                  <ArrowUpRight size={12} />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* All categories banner */}
        <div className="bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="block h-px w-10 bg-[#D4AF37]" />
              <span className="eyebrow">Toutes Nos Collections</span>
              <span className="block h-px w-10 bg-[#D4AF37]" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold">
              <span className="text-[#F4E8C1]">Quinze familles,</span>{' '}
              <span className="text-gold-gradient">un seul savoir-faire.</span>
            </h3>
            <p className="mt-3 text-sm text-[#F4E8C1]/60 max-w-2xl mx-auto font-light">
              De la maroquinerie aux nattes de prière, des chaussures aux
              fourrures : la maison MATAR STYLE CUIR couvre tout l'éventail du
              cuir d'exception.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {ALL_CATEGORIES.map((cat) => (
              <a
                key={cat.name}
                href="#collections"
                className="group flex flex-col items-center text-center p-4 border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all duration-300"
              >
                <div className="text-2xl mb-2 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all">
                  {cat.icon}
                </div>
                <div className="text-xs font-medium text-[#F4E8C1] group-hover:text-[#D4AF37] transition-colors leading-tight">
                  {cat.name}
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]/70 mt-1">
                  {cat.count} pièces
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Trust footer for products */}
        <div className="mt-14 grid sm:grid-cols-4 gap-4">
          {[
            { value: 'Pièce Unique', label: 'Chaque création est numérotée' },
            { value: 'Garantie à Vie', label: 'Réparation atelier à perpétuité' },
            { value: 'Sur Invitation', label: 'Visite privée de l\'atelier' },
            { value: 'Suivi à Vie', label: 'Patine et entretien offerts' },
          ].map((item) => (
            <div
              key={item.label}
              className="text-center p-4 border-l-2 border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors"
            >
              <div className="font-display text-sm font-semibold text-gold-gradient tracking-wide">
                {item.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55 mt-2">
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
